import { Button } from '@artosera/ui';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useNavigate, useRevalidator, useRouteError } from 'react-router';
import { useAuthentification } from '../../auth/useAuthentification.js';
import { demanderRetourLocal, derniereAdresseAffichee } from './horsLigne.js';
import { AuthCard } from '../AuthCard.js';
import { RAISON_HORS_LIGNE } from '../loaders.js';
import styles from './ErreurChargement.module.css';

/**
 * Élément d'erreur des routes à chargeur (`/`, `/:slug`) : sans réseau, la
 * session ne peut être ni vérifiée ni restituée du cache (relecture du
 * 23/09, constat 5). La page le dit clairement et propose de réessayer,
 * plutôt qu'une connexion qui échouerait aussi.
 *
 * Jamais d'impasse (constat F) : sans réseau, « Réessayer » ne sert à rien.
 * La page offre donc deux sorties LOCALES — revenir à la dernière page que la
 * coquille a affichée, ou se déconnecter (l'identité et le cache sont oubliés
 * même si l'API est injoignable).
 */
export function ErreurChargement(): JSX.Element {
  const { t } = useTranslation('regie');
  const erreur = useRouteError();
  const revalidateur = useRevalidator();
  const navigate = useNavigate();
  const { deconnecter } = useAuthentification();
  const derniere = derniereAdresseAffichee();
  const horsLigne =
    isRouteErrorResponse(erreur) &&
    (erreur.data as { raison?: unknown } | null)?.raison === RAISON_HORS_LIGNE;

  return (
    <AuthCard
      title={horsLigne ? t('horsLigne.titre') : t('horsLigne.erreurTitre')}
      subtitle={horsLigne ? t('horsLigne.sessionInjoignable') : t('horsLigne.erreurTexte')}
    >
      <div className={styles.actions}>
        <Button
          type="button"
          className={styles.action}
          loading={revalidateur.state === 'loading'}
          onClick={() => void revalidateur.revalidate()}
        >
          {t('horsLigne.reessayer')}
        </Button>
        {derniere ? (
          <Button
            type="button"
            variant="secondary"
            className={styles.action}
            onClick={() => {
              const adresse = demanderRetourLocal();
              if (adresse) void navigate(adresse);
            }}
          >
            {t('horsLigne.revenir')}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          className={styles.action}
          onClick={() => void deconnecter()}
        >
          {t('horsLigne.deconnecter')}
        </Button>
      </div>
    </AuthCard>
  );
}
